/**
 * Quick View Drawer Functionality - Fully Accessible
 * Handles drawer open/close, image carousel, AJAX product loading
 * Includes focus management and keyboard navigation for accessibility
 */

class QuickViewDrawer {
    constructor() {
        this.drawer = document.getElementById('quick-view-drawer');
        this.drawerBody = document.getElementById('quick-view-body');
        this.overlay = this.drawer?.querySelector('.quick-view-overlay');
        this.closeBtn = this.drawer?.querySelector('.quick-view-close');
        this.currentProductHandle = null;
        this.touchStartX = 0;
        this.touchEndX = 0;
        this.previouslyFocusedElement = null;
        this.focusableElements = [];
        
        this.init();
    }
    
    init() {
        if (!this.drawer) return;
        
        // Bind event listeners
        this.bindEvents();
        
        // Listen for quick view button clicks
        document.addEventListener('click', (e) => {
            const trigger = e.target.closest('.quick-view-trigger');
            if (trigger) {
                e.preventDefault();
                const productHandle = trigger.dataset.productHandle;
                const productId = trigger.dataset.productId;
                this.open(productHandle, productId);
            }
        });
    }
    
    bindEvents() {
        // Close on overlay click
        this.overlay?.addEventListener('click', () => this.close());
        
        // Close on close button click - use event delegation so it works after content replacement
        this.drawer?.addEventListener('click', (e) => {
            const closeBtn = e.target.closest('.quick-view-close');
            if (closeBtn) {
                e.preventDefault();
                e.stopPropagation();
                this.close();
            }
        });
        
        // Close on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.drawer.classList.contains('active')) {
                this.close();
            }
        });
        
        // Trap focus within drawer when open
        this.drawer?.addEventListener('keydown', (e) => {
            if (e.key === 'Tab' && this.drawer.classList.contains('active')) {
                this.trapFocus(e);
            }
        });
        
        // Prevent body scroll when drawer is open
        this.drawer.addEventListener('transitionend', () => {
            if (this.drawer.classList.contains('active')) {
                document.body.style.overflow = 'hidden';
            }
        });
    }
    
    async open(productHandle, productId) {
        if (!productHandle) return;
        
        // Store currently focused element to restore later
        this.previouslyFocusedElement = document.activeElement;
        
        // Explicitly blur the currently focused element (the Quick View button)
        // This prevents the aria-hidden warning
        if (document.activeElement) {
            document.activeElement.blur();
        }
        
        // DISABLE ATTENTIVE COMPLETELY
        this.disableAttentive();
        
        this.currentProductHandle = productHandle;
        this.drawer.classList.add('active');
        this.drawer.setAttribute('aria-hidden', 'false');
        
        // Move focus to drawer
        this.drawer.focus();
        
        // Hide main content from screen readers while drawer is open
        const mainContent = document.getElementById('MainContent');
        if (mainContent) {
            mainContent.setAttribute('aria-hidden', 'true');
        }
        
        // Show loading state
        this.showLoading();
        
        try {
            // Clear any existing SubscriptionWidgetConfig to avoid conflicts
            delete window.SubscriptionWidgetConfig;
            
            // Fetch the quick view template
            const response = await fetch(`/products/${productHandle}?view=quick-view`);
            
            if (!response.ok) {
                throw new Error('Failed to load product');
            }
            
            const html = await response.text();
            
            // Parse the response
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            
            // Extract ONLY the quick view content divs
            const qvCarousel = tempDiv.querySelector('.qv-image-carousel');
            const qvInfo = tempDiv.querySelector('.qv-product-info');
            
            if (qvCarousel && qvInfo) {
                // Use the clean quick view content
                this.drawerBody.innerHTML = qvCarousel.outerHTML + qvInfo.outerHTML;
            } else {
                // Fallback to full content if structure not found
                this.drawerBody.innerHTML = html;
            }
            
            // Initialize carousel
            this.initCarousel();
            
            // Initialize form handling
            this.initFormHandling();
            
            // Trigger any third-party scripts
            this.triggerExternalScripts();
            
            // Set focus to close button
            setTimeout(() => {
                this.closeBtn?.focus();
                this.updateFocusableElements();
            }, 100);
            
        } catch (error) {
            console.error('Quick view error:', error);
            this.showError();
        }
    }
    
    close() {
        this.drawer.classList.remove('active');
        this.drawer.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        document.body.style.pointerEvents = '';
        
        // RE-ENABLE ATTENTIVE
        this.enableAttentive();
        
        // Remove aria-hidden from main content
        const mainContent = document.getElementById('MainContent');
        if (mainContent) {
            mainContent.removeAttribute('aria-hidden');
            mainContent.style.pointerEvents = '';
        }
        
        // Ensure overlay is not blocking clicks
        if (this.overlay) {
            this.overlay.style.pointerEvents = 'none';
            this.overlay.style.display = 'none';
        }
        
        // Remove any stray overlays or blocking elements
        document.querySelectorAll('.quick-view-overlay').forEach(el => {
            el.style.pointerEvents = 'none';
            el.style.display = 'none';
        });
        
        // Restore focus to previously focused element
        if (this.previouslyFocusedElement) {
            this.previouslyFocusedElement.focus();
            this.previouslyFocusedElement = null;
        }
        
        // Clear content after animation
        setTimeout(() => {
            if (!this.drawer.classList.contains('active')) {
                this.drawerBody.innerHTML = '<div class="quick-view-loading" role="status" aria-live="polite" aria-label="Loading product details"><div class="loading-spinner" aria-hidden="true"><svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div><span class="visually-hidden">Loading product information</span></div>';
            }
        }, 300);
    }
    
    closeAttentivePopup() {
        // Close Attentive creative iframes and overlays
        const attentiveIframes = document.querySelectorAll('iframe[id*="attentive"], iframe[src*="attn.tv"], iframe[class*="attentive"]');
        attentiveIframes.forEach(iframe => {
            iframe.style.display = 'none';
            iframe.style.visibility = 'hidden';
            iframe.style.pointerEvents = 'none';
            if (iframe.parentElement) {
                iframe.parentElement.style.display = 'none';
                iframe.parentElement.style.pointerEvents = 'none';
            }
        });
        
        // Close any Attentive overlays/backdrops
        const attentiveOverlays = document.querySelectorAll('[class*="attentive"], [id*="attentive"], [class*="attn-"], [id*="attn-"]');
        attentiveOverlays.forEach(overlay => {
            if (overlay.tagName !== 'SCRIPT' && overlay.tagName !== 'LINK') {
                overlay.style.display = 'none';
                overlay.style.pointerEvents = 'none';
                overlay.style.visibility = 'hidden';
            }
        });
        
        // Try to call Attentive's close method if available
        if (window.attentive && typeof window.attentive.close === 'function') {
            try {
                window.attentive.close();
            } catch (e) {}
        }
        
        // Close Swell/Yotpo popups as well
        const swellIframes = document.querySelectorAll('iframe[src*="swell"], iframe[class*="swell"]');
        swellIframes.forEach(iframe => {
            iframe.style.display = 'none';
            iframe.style.pointerEvents = 'none';
        });
        
        // Remove any modal backdrops
        document.querySelectorAll('[class*="modal-backdrop"], [class*="overlay"], [class*="backdrop"]').forEach(backdrop => {
            if (!backdrop.classList.contains('quick-view-overlay')) {
                backdrop.style.display = 'none';
                backdrop.style.pointerEvents = 'none';
            }
        });
        
        // Reset body overflow in case Attentive set it
        document.body.style.overflow = '';
    }
    
    disableAttentive() {
        // Store original Attentive objects so we can restore them
        if (!this.attentiveBackup) {
            this.attentiveBackup = {
                __attentive: window.__attentive,
                attentive: window.attentive,
                _attentive: window._attentive
            };
        }
        
        // Replace all Attentive objects with disabled versions
        window.__attentive = {
            trigger: () => false,
            show: () => false,
            showCreative: () => false,
            analytics: { track: () => false },
            disabled: true
        };
        
        window.attentive = {
            trigger: () => false,
            show: () => false,
            showCreative: () => false,
            analytics: { track: () => false },
            close: () => false,
            disabled: true
        };
        
        window._attentive = {
            trigger: () => false,
            show: () => false,
            disabled: true
        };
        
        // Set global flag
        window.__attentive_disabled_by_quickview = true;
        
        // Hide any existing Attentive elements
        this.closeAttentivePopup();
    }
    
    enableAttentive() {
        // Restore original Attentive objects
        if (this.attentiveBackup) {
            if (this.attentiveBackup.__attentive) {
                window.__attentive = this.attentiveBackup.__attentive;
            }
            if (this.attentiveBackup.attentive) {
                window.attentive = this.attentiveBackup.attentive;
            }
            if (this.attentiveBackup._attentive) {
                window._attentive = this.attentiveBackup._attentive;
            }
        }
        
        // Remove global flag
        delete window.__attentive_disabled_by_quickview;
    }
    
    async showFreeGiftSelection() {
        // Hide close button during free gift selection
        const closeBtn = this.drawer.querySelector('.quick-view-close');
        if (closeBtn) {
            closeBtn.style.display = 'none';
        }
        
        // Show loading state
        this.drawerBody.innerHTML = `
            <div style="padding: 60px 20px; text-align: center;">
                <div class="loading-spinner" style="margin: 0 auto 20px;">
                    <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" style="width: 40px; height: 40px;">
                        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                    </svg>
                </div>
                <p>Loading your free gift options...</p>
            </div>
        `;
        
        try {
            // Try multiple URL patterns
            const urls = [
                '/pages/quick-view-freegift?view=freegift',
                '/pages/quick-view-freegift'
            ];
            
            let response = null;
            
            for (const url of urls) {
                try {
                    response = await fetch(url);
                    if (response.ok) break;
                } catch (e) {}
            }
            
            if (!response || !response.ok) {
                console.warn('Quick View: Could not load free gift page, showing add-ons instead');
                this.showAddOnsUpsell();
                return;
            }
            
            const html = await response.text();
            
            if (html.length < 100) {
                console.warn('Quick View: Free gift HTML too short');
                this.showAddOnsUpsell();
                return;
            }
            
            // Replace drawer content with free gift selection
            this.drawerBody.innerHTML = html;
            
            // Store reference to this instance for the continue button
            window.quickViewInstance = this;
            
            // Re-initialize scripts
            const scripts = this.drawerBody.querySelectorAll('script');
            scripts.forEach(script => {
                const newScript = document.createElement('script');
                if (script.src) {
                    newScript.src = script.src;
                } else {
                    newScript.textContent = script.textContent;
                }
                script.parentNode.replaceChild(newScript, script);
            });
            
        } catch (error) {
            console.error('Quick View: Error loading free gift:', error);
            this.showAddOnsUpsell();
        }
    }
    
    async showAddOnsUpsell() {
        // Show close button again when on add-ons screen
        const closeBtn = this.drawer.querySelector('.quick-view-close');
        if (closeBtn) {
            closeBtn.style.display = '';
        }
        
        // Show loading state
        this.drawerBody.innerHTML = `
            <div style="padding: 60px 20px; text-align: center;">
                <div class="loading-spinner" style="margin: 0 auto 20px;">
                    <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" style="width: 40px; height: 40px;">
                        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                    </svg>
                </div>
                <p>Loading add-ons...</p>
            </div>
        `;
        
        try {
            // Try multiple URL patterns
            const urls = [
                '/pages/quick-view-addons?view=addons',
                '/pages/quick-view-addons',
                '/collections/pre-checkout-add-ons?view=addons'
            ];
            
            let response = null;
            let successUrl = null;
            
            for (const url of urls) {
                try {
                    response = await fetch(url);
                    if (response.ok) {
                        successUrl = url;
                        break;
                    }
                } catch (e) {}
            }
            
            if (!response || !response.ok) {
                console.warn('Quick View: Could not load add-ons from any URL, closing drawer');
                this.close();
                return;
            }
            
            const html = await response.text();
            
            // Check if we got actual content
            if (html.length < 100) {
                console.warn('Quick View: HTML too short, likely empty page');
                this.close();
                return;
            }
            
            // Replace drawer content with add-ons
            this.drawerBody.innerHTML = html;
            
            // Re-initialize any scripts in the new content
            const scripts = this.drawerBody.querySelectorAll('script');
            scripts.forEach(script => {
                const newScript = document.createElement('script');
                if (script.src) {
                    newScript.src = script.src;
                } else {
                    newScript.textContent = script.textContent;
                }
                script.parentNode.replaceChild(newScript, script);
            });
            
        } catch (error) {
            console.error('Quick View: Error loading add-ons:', error);
            this.close();
        }
    }
    
    showLoading() {
        this.drawerBody.innerHTML = `
            <div class="quick-view-loading" role="status" aria-live="polite" aria-label="Loading product details">
                <div class="loading-spinner" aria-hidden="true">
                    <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                    </svg>
                </div>
                <span class="visually-hidden">Loading product information</span>
            </div>
        `;
    }
    
    showError() {
        this.drawerBody.innerHTML = `
            <div style="padding: 40px; text-align: center;" role="alert">
                <p style="margin-bottom: 20px;">Sorry, we couldn't load this product.</p>
                <button onclick="document.getElementById('quick-view-drawer').classList.remove('active')" class="button">Close</button>
            </div>
        `;
    }
    
    updateFocusableElements() {
        const focusableSelectors = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        this.focusableElements = Array.from(this.drawer.querySelectorAll(focusableSelectors));
    }
    
    trapFocus(e) {
        if (this.focusableElements.length === 0) {
            this.updateFocusableElements();
        }
        
        const firstFocusable = this.focusableElements[0];
        const lastFocusable = this.focusableElements[this.focusableElements.length - 1];
        
        if (e.shiftKey) {
            // Shift + Tab
            if (document.activeElement === firstFocusable) {
                e.preventDefault();
                lastFocusable?.focus();
            }
        } else {
            // Tab
            if (document.activeElement === lastFocusable) {
                e.preventDefault();
                firstFocusable?.focus();
            }
        }
    }
    
    initCarousel() {
        const carousel = this.drawerBody.querySelector('.qv-image-carousel');
        if (!carousel) return;
        
        const track = carousel.querySelector('.qv-carousel-track');
        const slides = carousel.querySelectorAll('.qv-carousel-slide');
        const prevBtn = carousel.querySelector('.qv-carousel-btn.prev');
        const nextBtn = carousel.querySelector('.qv-carousel-btn.next');
        
        if (!track || slides.length === 0) return;
        
        let currentSlide = 0;
        const totalSlides = slides.length;
        
        const updateCarousel = () => {
            // Calculate transform to center current slide
            // Each slide is 84% width, so we need to shift by 84% per slide
            // Plus offset by 8% (half of 16%) to center the current slide
            const offset = 8; // Percentage to offset for centering
            const slideWidth = 84; // Percentage width of each slide
            track.style.transform = `translateX(calc(${offset}% - ${currentSlide * slideWidth}%))`;
            track.setAttribute('data-current-slide', currentSlide);
            
            // Update active class on slides
            slides.forEach((slide, index) => {
                if (index === currentSlide) {
                    slide.classList.add('active');
                } else {
                    slide.classList.remove('active');
                }
            });
            
            // Update button states
            if (prevBtn) {
                prevBtn.disabled = currentSlide === 0;
                prevBtn.setAttribute('aria-disabled', currentSlide === 0);
            }
            if (nextBtn) {
                nextBtn.disabled = currentSlide === totalSlides - 1;
                nextBtn.setAttribute('aria-disabled', currentSlide === totalSlides - 1);
            }
            
            // Announce to screen readers
            const announcement = `Viewing image ${currentSlide + 1} of ${totalSlides}`;
            this.announceToScreenReader(announcement);
        };
        
        const goToSlide = (index) => {
            currentSlide = Math.max(0, Math.min(index, totalSlides - 1));
            updateCarousel();
        };
        
        // Button navigation
        prevBtn?.addEventListener('click', () => goToSlide(currentSlide - 1));
        nextBtn?.addEventListener('click', () => goToSlide(currentSlide + 1));
        
        // Keyboard navigation for carousel
        carousel.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                goToSlide(currentSlide - 1);
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                goToSlide(currentSlide + 1);
            }
        });
        
        // Touch/swipe navigation
        track.addEventListener('touchstart', (e) => {
            this.touchStartX = e.changedTouches[0].screenX;
        }, { passive: true });
        
        track.addEventListener('touchend', (e) => {
            this.touchEndX = e.changedTouches[0].screenX;
            this.handleSwipe(carousel);
        }, { passive: true });
        
        // Mouse drag navigation (optional)
        let isDragging = false;
        let startX = 0;
        
        track.addEventListener('mousedown', (e) => {
            isDragging = true;
            startX = e.pageX;
            track.style.cursor = 'grabbing';
        });
        
        document.addEventListener('mouseup', () => {
            if (isDragging) {
                isDragging = false;
                track.style.cursor = 'grab';
            }
        });
        
        track.addEventListener('mousemove', (e) => {
            if (!isDragging) return;
            e.preventDefault();
            const x = e.pageX;
            const walk = (x - startX) * 2;
            
            if (Math.abs(walk) > 50) {
                if (walk < 0) {
                    goToSlide(currentSlide + 1);
                } else {
                    goToSlide(currentSlide - 1);
                }
                isDragging = false;
            }
        });
        
        // Initialize
        updateCarousel();
    }
    
    handleSwipe(carousel) {
        const swipeThreshold = 50;
        const diff = this.touchStartX - this.touchEndX;
        
        if (Math.abs(diff) > swipeThreshold) {
            const track = carousel?.querySelector('.qv-carousel-track');
            const slides = carousel?.querySelectorAll('.qv-carousel-slide');
            
            if (!track || !slides) return;
            
            let currentSlide = parseInt(track.dataset.currentSlide || 0);
            
            if (diff > 0 && currentSlide < slides.length - 1) {
                // Swipe left - next slide
                currentSlide++;
            } else if (diff < 0 && currentSlide > 0) {
                // Swipe right - previous slide
                currentSlide--;
            }
            
            track.dataset.currentSlide = currentSlide;
            
            // Calculate transform with peek effect
            const offset = 8;
            const slideWidth = 84;
            track.style.transform = `translateX(calc(${offset}% - ${currentSlide * slideWidth}%))`;
            
            // Update active class
            slides.forEach((slide, index) => {
                if (index === currentSlide) {
                    slide.classList.add('active');
                } else {
                    slide.classList.remove('active');
                }
            });
            
            // Update buttons
            const prevBtn = carousel.querySelector('.qv-carousel-btn.prev');
            const nextBtn = carousel.querySelector('.qv-carousel-btn.next');
            if (prevBtn) {
                prevBtn.disabled = currentSlide === 0;
                prevBtn.setAttribute('aria-disabled', currentSlide === 0);
            }
            if (nextBtn) {
                nextBtn.disabled = currentSlide === slides.length - 1;
                nextBtn.setAttribute('aria-disabled', currentSlide === slides.length - 1);
            }
            
            // Announce to screen readers
            const announcement = `Viewing image ${currentSlide + 1} of ${slides.length}`;
            this.announceToScreenReader(announcement);
        }
    }
    
    announceToScreenReader(message) {
        const announcement = document.createElement('div');
        announcement.setAttribute('role', 'status');
        announcement.setAttribute('aria-live', 'polite');
        announcement.setAttribute('aria-atomic', 'true');
        announcement.className = 'visually-hidden';
        announcement.textContent = message;
        
        document.body.appendChild(announcement);
        
        setTimeout(() => {
            document.body.removeChild(announcement);
        }, 1000);
    }
    
    initFormHandling() {
        const form = this.drawerBody.querySelector('form[action="/cart/add"]');
        if (!form) {
            console.log('Quick View: No form found');
            return;
        }
        
        // Get product ID from form - handle multiple ways
        let productId = null;
        
        if (form.id && typeof form.id === 'string') {
            const match = form.id.match(/qv-add-to-cart-(\d+)/);
            productId = match ? match[1] : null;
        }
        
        // If we can't get it from form ID, try to find it from the variant input
        if (!productId) {
            const variantInput = form.querySelector('input[name="id"]');
            if (variantInput && variantInput.id) {
                const match = variantInput.id.match(/qv-variant-id-(\d+)/);
                productId = match ? match[1] : null;
            }
        }
        
        // If still no product ID, try finding from any element with product ID
        if (!productId) {
            const carousel = this.drawerBody.querySelector('.qv-image-carousel');
            if (carousel) {
                productId = carousel.dataset.productId;
            }
        }
        
        if (!productId) {
            console.warn('Quick View: Could not extract product ID, subscription widget may not work');
            // Continue anyway - at least one-time purchase will work
        }
        
        console.log('Quick View: Initializing form for product ID:', productId);
        
        // Setup quantity buttons
        this.drawerBody.querySelectorAll('.quantity__button').forEach(btn => {
            btn.addEventListener('click', () => {
                const input = btn.parentElement.querySelector('.quantity__input');
                const hiddenInput = productId ? this.drawerBody.querySelector(`#qv-quantity-hidden-${productId}`) : null;
                const currentValue = parseInt(input.value) || 1;
                
                if (btn.dataset.action === 'plus') {
                    input.value = currentValue + 1;
                } else if (btn.dataset.action === 'minus' && currentValue > 1) {
                    input.value = currentValue - 1;
                }
                
                if (hiddenInput) {
                    hiddenInput.value = input.value;
                }
            });
        });
        
        // Setup variant selector
        const variantSelect = productId ? this.drawerBody.querySelector(`#qv-variant-select-${productId}`) : null;
        if (variantSelect) {
            variantSelect.addEventListener('change', function() {
                const selectedOption = this.options[this.selectedIndex];
                const priceText = selectedOption.dataset.price;
                const priceEl = document.getElementById(`qv-price-${productId}`);
                const variantIdInput = document.getElementById(`qv-variant-id-${productId}`);
                
                if (priceEl) {
                    priceEl.querySelector('.price-item').innerText = priceText;
                }
                
                if (variantIdInput) {
                    variantIdInput.value = this.value;
                }
            });
        }
        
        // =============================================
        // Setup subscription widget
        // =============================================
        if (productId) {
            console.log('Quick View: Initializing subscription widget for product', productId);
            
            const subDot = this.drawerBody.querySelector(`#qv-sub-dot-${productId}`);
            const subRing = this.drawerBody.querySelector(`#qv-sub-ring-${productId}`);
            const onetimeDot = this.drawerBody.querySelector(`#qv-onetime-dot-${productId}`);
            const onetimeRing = this.drawerBody.querySelector(`#qv-onetime-ring-${productId}`);
            const subscribeLabel = this.drawerBody.querySelector(`#qv-subscribe-label-${productId}`);
            const onetimeLabel = this.drawerBody.querySelector(`#qv-onetime-label-${productId}`);
            const frequencyEl = this.drawerBody.querySelector(`#qv-frequency-${productId}`);
            const benefitsEl = this.drawerBody.querySelector(`#qv-benefits-${productId}`);
            const sellingPlanInput = this.drawerBody.querySelector(`#qv-selected-selling-plan-${productId}`);
            const planSelect = this.drawerBody.querySelector(`#qv-selling-plan-${productId}`);
            
            console.log('Subscription elements found:', {
                subDot: !!subDot,
                subscribeLabel: !!subscribeLabel,
                onetimeLabel: !!onetimeLabel,
                frequencyEl: !!frequencyEl,
                benefitsEl: !!benefitsEl
            });
            
            const setSubscriptionMode = (isSubscription) => {
                console.log('Setting subscription mode:', isSubscription ? 'SUBSCRIPTION' : 'ONE-TIME');
                
                // Toggle card backgrounds
                if (subscribeLabel) {
                    if (isSubscription) {
                        subscribeLabel.classList.add('is-selected');
                    } else {
                        subscribeLabel.classList.remove('is-selected');
                    }
                }
                if (onetimeLabel) {
                    if (isSubscription) {
                        onetimeLabel.classList.remove('is-selected');
                    } else {
                        onetimeLabel.classList.add('is-selected');
                    }
                }
                
                // Toggle SVG dots
                if (subDot) {
                    subDot.style.opacity = isSubscription ? '1' : '0';
                }
                if (onetimeDot) {
                    onetimeDot.style.opacity = isSubscription ? '0' : '1';
                }
                
                // Toggle ring colors
                if (subRing) {
                    subRing.setAttribute('stroke', isSubscription ? '#619a2c' : '#b0b0b0');
                }
                if (onetimeRing) {
                    onetimeRing.setAttribute('stroke', isSubscription ? '#b0b0b0' : '#619a2c');
                }
                
                // Show/hide subscribe-only content
                if (frequencyEl) {
                    frequencyEl.style.display = isSubscription ? 'block' : 'none';
                    console.log('Frequency display:', frequencyEl.style.display);
                }
                if (benefitsEl) {
                    benefitsEl.style.display = isSubscription ? 'block' : 'none';
                    console.log('Benefits display:', benefitsEl.style.display);
                }
                
                // Update selling plan
                if (sellingPlanInput) {
                    if (isSubscription && planSelect) {
                        sellingPlanInput.value = planSelect.value;
                    } else {
                        sellingPlanInput.value = '';
                    }
                }
            };
            
            // Click handlers
            if (subscribeLabel) {
                subscribeLabel.addEventListener('click', (e) => {
                    console.log('Subscribe label clicked, target:', e.target.tagName);
                    // Don't trigger if clicking on dropdown
                    if (e.target.tagName === 'SELECT' || e.target.tagName === 'OPTION') {
                        console.log('Ignoring click on:', e.target.tagName);
                        return;
                    }
                    setSubscriptionMode(true);
                });
            }
            
            if (onetimeLabel) {
                onetimeLabel.addEventListener('click', () => {
                    console.log('One-time label clicked');
                    setSubscriptionMode(false);
                });
            }
            
            // Update selling plan when dropdown changes
            if (planSelect && sellingPlanInput) {
                planSelect.addEventListener('change', (e) => {
                    console.log('Plan changed to:', planSelect.value);
                    e.stopPropagation();
                    sellingPlanInput.value = planSelect.value;
                });
            }
            
            // Set initial state to subscription
            console.log('Setting initial subscription state');
            setSubscriptionMode(true);
        }
        
        // Handle form submission
        form.addEventListener('submit', async (e) => {
            e.preventDefault();
            
            console.log('Quick View: Form submitting...');
            
            // Build form data
            const formData = new FormData(form);
            
            // Add selling plan if subscription is selected (only if we have productId)
            if (productId) {
                const selectedPurchaseType = this.drawerBody.querySelector(`input[name="qv-purchase-type-${productId}"]:checked`);
                
                if (selectedPurchaseType && selectedPurchaseType.value === 'subscription') {
                    const planSelect = this.drawerBody.querySelector(`#qv-selling-plan-${productId}`);
                    if (planSelect && planSelect.value) {
                        formData.append('selling_plan', planSelect.value);
                        console.log('Quick View: Adding subscription with plan:', planSelect.value);
                    } else {
                        console.warn('Quick View: Subscription selected but no plan found');
                    }
                } else {
                    console.log('Quick View: One-time purchase selected');
                }
            }
            
            const submitBtn = form.querySelector('button[type="submit"]');
            const originalText = submitBtn?.textContent;
            
            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.setAttribute('aria-disabled', 'true');
                submitBtn.textContent = 'Adding...';
                this.announceToScreenReader('Adding item to cart');
            }
            
            try {
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    body: formData
                });
                
                if (!response.ok) {
                    throw new Error('Failed to add to cart');
                }
                
                const data = await response.json();
                
                // Success - show feedback
                if (submitBtn) {
                    submitBtn.textContent = 'Added!';
                    submitBtn.style.backgroundColor = '#4caf50';
                    this.announceToScreenReader('Item added to cart successfully');
                }
                
                // Update cart icon directly (don't dispatch events that trigger Klaviyo)
                fetch('/cart.js')
                    .then(response => response.json())
                    .then(cart => {
                        // Update cart count bubble
                        const cartBubble = document.querySelector('#cart-icon-bubble');
                        if (cartBubble) {
                                let countBubbleContainer = cartBubble.querySelector('.cart-count-bubble');
                                
                                // If bubble doesn't exist (cart was empty), create it
                                if (!countBubbleContainer && cart.item_count > 0) {
                                    const iconDiv = cartBubble.querySelector('.icon_img_div');
                                    if (iconDiv) {
                                        countBubbleContainer = document.createElement('div');
                                        countBubbleContainer.className = 'cart-count-bubble';
                                        
                                        const countSpan = document.createElement('span');
                                        countSpan.setAttribute('data-cart-count', '');
                                        countSpan.setAttribute('aria-hidden', 'true');
                                        countSpan.textContent = cart.item_count;
                                        
                                        const visuallyHiddenSpan = document.createElement('span');
                                        visuallyHiddenSpan.className = 'visually-hidden';
                                        visuallyHiddenSpan.textContent = `${cart.item_count} item${cart.item_count !== 1 ? 's' : ''}`;
                                        
                                        countBubbleContainer.appendChild(countSpan);
                                        countBubbleContainer.appendChild(visuallyHiddenSpan);
                                        iconDiv.appendChild(countBubbleContainer);
                                    }
                                } else if (countBubbleContainer) {
                                    // Bubble exists, just update the count
                                    const countSpan = countBubbleContainer.querySelector('[data-cart-count]');
                                    if (countSpan) {
                                        countSpan.textContent = cart.item_count;
                                    }
                                    
                                    const visuallyHiddenSpan = countBubbleContainer.querySelector('.visually-hidden');
                                    if (visuallyHiddenSpan) {
                                        visuallyHiddenSpan.textContent = `${cart.item_count} item${cart.item_count !== 1 ? 's' : ''}`;
                                    }
                                    
                                    // Show or hide based on count
                                    if (cart.item_count > 0) {
                                        countBubbleContainer.style.display = '';
                                    } else {
                                        countBubbleContainer.style.display = 'none';
                                    }
                                }
                                
                                // Also swap the cart icon from empty to full if needed
                                const iconDiv = cartBubble.querySelector('.icon_img_div');
                                if (iconDiv && cart.item_count > 0) {
                                    const emptyIcon = iconDiv.querySelector('.icon-cart-empty');
                                    const fullIcon = iconDiv.querySelector('.icon-cart');
                                    
                                    if (emptyIcon) {
                                        emptyIcon.style.display = 'none';
                                    }
                                    if (fullIcon) {
                                        fullIcon.style.display = 'block';
                                    } else if (emptyIcon) {
                                        // Clone the empty icon and change it to the full cart icon
                                        const newFullIcon = emptyIcon.cloneNode(true);
                                        newFullIcon.classList.remove('icon-cart-empty');
                                        newFullIcon.classList.add('icon-cart');
                                        newFullIcon.style.display = 'block';
                                        if (newFullIcon.hasAttribute('data-src')) {
                                            newFullIcon.src = newFullIcon.getAttribute('data-src');
                                        }
                                        iconDiv.appendChild(newFullIcon);
                                    }
                                }
                            }
                            
                        })
                        .catch(err => console.error('Quick View: Cart refresh error:', err));
                
                // After successful add, either show add-ons (subscription) or close drawer (one-time)
                setTimeout(() => {
                    // Force close any Attentive popups and overlays
                    this.closeAttentivePopup();
                    
                    // Check if this was a subscription purchase
                    const selectedPurchaseType = this.drawerBody.querySelector(`input[name^="qv-purchase-type-"]:checked`);
                    const isSubscription = selectedPurchaseType && selectedPurchaseType.value === 'subscription';
                    
                    if (isSubscription) {
                        // Show add-ons for subscription customers
                        this.showAddOnsUpsell();
                    } else {
                        // Close drawer for one-time purchases after showing "Added!" feedback
                        setTimeout(() => {
                            this.close();
                        }, 1500);
                    }
                }, 1000);
                
            } catch (error) {
                console.error('Quick View: Add to cart error:', error);
                
                if (submitBtn) {
                    submitBtn.textContent = 'Error - Try Again';
                    submitBtn.style.backgroundColor = '#f44336';
                    submitBtn.disabled = false;
                    submitBtn.setAttribute('aria-disabled', 'false');
                    this.announceToScreenReader('Error adding to cart. Please try again.');
                    
                    setTimeout(() => {
                        submitBtn.textContent = originalText;
                        submitBtn.style.backgroundColor = '';
                    }, 2000);
                }
            }
        });
        
        console.log('Quick View: Form handling initialized');
    }
    
    triggerExternalScripts() {
        // Trigger Yotpo refresh
        if (typeof yotpo !== 'undefined' && yotpo.refreshWidgets) {
            yotpo.refreshWidgets();
        }
        
        console.log('Quick View: External scripts initialization complete');
    }
    
    // Re-render web component by cloning and replacing
    // Source: ReCharge documentation
    reRenderWebComponent(element) {
        if (!element) return;
        
        const newEl = element.cloneNode(true);
        element.replaceWith(newEl);
    }
    
    // Re-execute all script tags to force reload
    // Source: ReCharge documentation
    reExecuteScripts(container, attrReplace = (attr) => attr.value) {
        const scripts = container.querySelectorAll('script');
        
        scripts.forEach((oldScript) => {
            const newScript = document.createElement('script');
            
            // Copy attributes
            for (const attr of oldScript.attributes) {
                try {
                    newScript.setAttribute(attr.name, attrReplace(attr));
                } catch (e) {
                    console.warn('Quick View: Could not set attribute', attr.name, e);
                }
            }
            
            // Copy inline code if any
            if (oldScript.textContent) {
                newScript.textContent = oldScript.textContent;
            }
            
            // Replace the old script with the new one
            try {
                oldScript.parentNode.replaceChild(newScript, oldScript);
            } catch (e) {
                console.error('Quick View: Error replacing script:', e);
            }
        });
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new QuickViewDrawer();
    });
} else {
    new QuickViewDrawer();
}